// client/src/utils/avatar.js
//
// Утилиты для аватарок пользователя:
//   - getAvatarSrc(access) — DiceBear identicon (fallback) или кастомный base64
//   - resizeImageToBase64(file) — клиентский resize до 128×128 JPEG q=0.8
//   - validateAvatarFile(file) — проверка типа и размера
//

// ============================================================================
// Константы
// ============================================================================

const DICEBEAR_BASE = 'https://api.dicebear.com/10.x/identicon/svg';

const MAX_FILE_BYTES = 2 * 1024 * 1024; // 2 MB до сжатия
const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
const TARGET_SIZE = 128;        // 128×128 px
const JPEG_QUALITY = 0.8;       // ~5–10 KB

// ============================================================================
// Основная логика
// ============================================================================

/**
 * Возвращает URL аватара для отображения.
 *
 * Приоритет:
 *   1. access.avatar_base64 (кастомный, загруженный пользователем)
 *   2. DiceBear identicon по seed (kratos_identity_id → email → 'uef')
 *
 * @param {object|null} access — state.auth.access (или его часть)
 * @param {object} [opts]
 * @param {string} [opts.style='identicon'] — стиль DiceBear
 * @returns {string} — URL (data:image/... или https://api.dicebear.com/...)
 */
export function getAvatarSrc(access, opts = {}) {
  const { style = 'identicon' } = opts;

  if (access?.avatar_base64 && typeof access.avatar_base64 === 'string') {
    return access.avatar_base64;
  }

  const seed =
    access?.kratos_identity_id ||
    access?.player_id ||
    access?.email ||
    access?.display_name ||
    'uef';

  const base = style === 'identicon' ? DICEBEAR_BASE : `https://api.dicebear.com/10.x/${style}/svg`;

  return `${base}?seed=${encodeURIComponent(String(seed))}`;
}

/**
 * Проверяет, является ли файл допустимым изображением.
 *
 * @param {File} file
 * @returns {{ok: boolean, error?: string}}
 */
export function validateAvatarFile(file) {
  if (!file) {
    return { ok: false, error: 'Файл не выбран' };
  }
  if (!ALLOWED_MIME_TYPES.includes(file.type)) {
    return { ok: false, error: 'Допустимые форматы: JPEG, PNG, WebP' };
  }
  if (file.size > MAX_FILE_BYTES) {
    const mb = (file.size / 1024 / 1024).toFixed(1);
    return { ok: false, error: `Файл слишком большой (${mb} MB). Максимум 2 MB.` };
  }
  return { ok: true };
}

/**
 * Ресайзит изображение до 128×128 (авто-кроп по центру), сжимает в JPEG q=0.8,
 * возвращает data URI (`data:image/jpeg;base64,...`).
 *
 * @param {File} file
 * @returns {Promise<string>} — data URI
 */
export function resizeImageToBase64(file) {
  return new Promise((resolve, reject) => {
    const validation = validateAvatarFile(file);
    if (!validation.ok) {
      reject(new Error(validation.error));
      return;
    }

    const reader = new FileReader();

    reader.onerror = () => reject(new Error('Не удалось прочитать файл'));

    reader.onload = (e) => {
      const img = new Image();

      img.onerror = () => reject(new Error('Не удалось загрузить изображение'));

      img.onload = () => {
        try {
          const dataUri = renderToSquareJpeg(img, TARGET_SIZE, JPEG_QUALITY);
          resolve(dataUri);
        } catch (err) {
          reject(err);
        }
      };

      img.src = e.target.result;
    };

    reader.readAsDataURL(file);
  });
}

/**
 * Рендерит изображение в квадрат `size × size` JPEG.
 * Авто-кроп по центру (shortest side).
 *
 * @param {HTMLImageElement} img
 * @param {number} size
 * @param {number} quality
 * @returns {string}
 */
function renderToSquareJpeg(img, size, quality) {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;

  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context недоступен');

  // Белый фон (JPEG не поддерживает прозрачность)
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, size, size);

  const srcW = img.naturalWidth || img.width;
  const srcH = img.naturalHeight || img.height;
  const srcMin = Math.min(srcW, srcH);

  const sx = (srcW - srcMin) / 2;
  const sy = (srcH - srcMin) / 2;

  ctx.drawImage(img, sx, sy, srcMin, srcMin, 0, 0, size, size);

  return canvas.toDataURL('image/jpeg', quality);
}

// ============================================================================
// Экспорт констант (для UI)
// ============================================================================

export const AVATAR_CONSTANTS = {
  MAX_FILE_BYTES,
  ALLOWED_MIME_TYPES,
  TARGET_SIZE,
  JPEG_QUALITY,
  DICEBEAR_BASE,
};
